import { Alert } from '@mm/components';
import { useChatbotCRUDActions } from '@mm/state/actions/ChatbotCRUDActions';
import { ChannelName } from '@mm/state/model/ChatbotModel';
import { selectActiveFacebookPage } from '@mm/state/selectors/facebookPageSelector';
import { activeChatbotSelector } from '@mm/state/selectors/viewSelectors';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ToggleBotLive } from '../../../../../../../common/components/ToggleBotLive/ToggleBotLive';
import { useBotChannels } from '../../../../../../../common/hooks/use-bot-channels';
import { ChatbotChannelsCheckmarks } from '../../../../components/ChatbotChannelsCheckmarks/ChatbotChannelsCheckmarks';
import SettingSection from '../../../../components/SettingSection/SettingSection';
import './ChatbotStatusSection.pcss';

const paragraphText = `
  When your chatbot is inactive, it won't be connected to your Facebook Page.
  No messages will be sent from this bot, and you can freely make changes and updates to it.
  When your chatbot is live you can still continue to make changes.
`;

type Props = {
  css?: {
    container?: string;
    channel?: string;
  };
  disabled?: boolean;
  onSwitch?: () => void;
  onSuccess?: () => void;
};

export const ChatbotChannelsToggle = ({
  css,
  onSwitch = () => {},
  onSuccess = () => {},
  disabled,
}: Props) => {
  const chatbotCrudActions = useChatbotCRUDActions();

  const chatbot = useSelector(activeChatbotSelector);
  const connectedPage = useSelector(selectActiveFacebookPage);

  useEffect(() => {
    chatbotCrudActions.retrieve(chatbot.id);
  }, [
    connectedPage?.smsActivated,
    connectedPage?.instagramActivated,
    connectedPage?.webChatActivated,
  ]);

  const { isChannelActive, toggleChannel } = useBotChannels();

  const [busy, setBusy] = useState(false);

  const onChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    onSwitch();
    const { name, checked } = e.target;
    setBusy(true);
    try {
      await toggleChannel(name as ChannelName, checked);
      onSuccess();
    } catch (err) {
    } finally {
      setBusy(false);
    }
  };

  return (
    <ChatbotChannelsCheckmarks
      css={css}
      disabled={disabled || busy}
      onToggle={onChange}
      isChecked={isChannelActive}
    />
  );
};

const ChatbotStatusSection = () => {
  const chatbot = useSelector(activeChatbotSelector);

  return (
    <SettingSection
      title="Chatbot Status"
      titleChildren={
        <>
          {chatbot && (
            <ToggleBotLive className="chatbot-status__toggle" bot={chatbot} />
          )}
        </>
      }
    >
      <div className="chatbot-status-section__body">
        <Alert showCloseIcon={false} type="info">
          {paragraphText}
        </Alert>

        <ChatbotChannelsToggle />
      </div>
    </SettingSection>
  );
};

export default ChatbotStatusSection;
